import {
  customerAddressFormToPayload,
  needsManualAddressSplit,
  toCustomerAddressFormValue,
  type CustomerAddressFormValue,
} from "@/modules/customers/domain/address";
import { Phone, UserIcon } from "@/components/icons";
import { Button, Input, Textarea, toast } from "@/components/kit";
import { Modal } from "@/components/modal";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import type { CustomerProfileDTO } from "@/modules/customers/domain/types";
import { useEffect, useState } from "react";
import { useUpdateCustomer } from "../api/queries";
import { CustomerAddressFields } from "./CustomerAddressFields";

type CustomerEditDialogProps = {
  customer: CustomerProfileDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CustomerEditDialog({
  customer,
  open,
  onOpenChange,
}: CustomerEditDialogProps) {
  const updateCustomer = useUpdateCustomer(customer.id);
  const [name, setName] = useState(customer.name);
  const [phone, setPhone] = useState(customer.phone ?? "");
  const [addressForm, setAddressForm] = useState<CustomerAddressFormValue>(
    () => toCustomerAddressFormValue(customer),
  );
  const [note, setNote] = useState(customer.note ?? "");

  useEffect(() => {
    if (!open) return;
    setName(customer.name);
    setPhone(customer.phone ?? "");
    setAddressForm(toCustomerAddressFormValue(customer));
    setNote(customer.note ?? "");
  }, [open, customer]);

  function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກຄ້າ");
      return;
    }

    updateCustomer.mutate(
      {
        name: trimmedName,
        phone: phone.trim() || null,
        ...customerAddressFormToPayload(addressForm),
        note: note.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກຂໍ້ມູນລູກຄ້າແລ້ວ");
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error("ບັນທຶກບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  const showLegacyHint = needsManualAddressSplit(customer);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ແກ້ໄຂຂໍ້ມູນລູກຄ້າ"
      description="ຊື່ ເບີໂທ ທີ່ຢູ່ ແລະ ໝາຍເຫດ"
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={updateCustomer.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            className="rounded-full"
            isLoading={updateCustomer.isPending}
            onClick={handleSave}
          >
            ບັນທຶກ
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FieldLayout>
          <FieldLabel htmlFor="cust-edit-name">
            <UserIcon className="size-4" /> ຊື່ລູກຄ້າ
          </FieldLabel>
          <FieldContent>
            <Input
              id="cust-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FieldContent>
        </FieldLayout>

        <FieldLayout>
          <FieldLabel htmlFor="cust-edit-phone">
            <Phone className="size-4" /> ເບີໂທ
          </FieldLabel>
          <FieldContent>
            <Input
              id="cust-edit-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
            />
          </FieldContent>
        </FieldLayout>

        <CustomerAddressFields
          value={addressForm}
          onChange={setAddressForm}
          idPrefix="cust-edit"
          legacyAddressHint={showLegacyHint ? customer.address : null}
        />

        <FieldLayout>
          <FieldLabel htmlFor="cust-edit-note">ໝາຍເຫດ</FieldLabel>
          <FieldContent>
            <Textarea
              id="cust-edit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="ໝາຍເຫດເພີ່ມເຕີມ..."
            />
          </FieldContent>
        </FieldLayout>
      </div>
    </Modal>
  );
}
